import { useMutation, useQuery } from '@w/react-query'
import { Button } from '@w/ui/components/button'
import { reapplyDeadLetter } from '../databases/database-renames'
import type { SpaceResource } from '../router/spaces-resources'

export function SyncProblems({ resource }: { resource: SpaceResource }) {
  const problems = useQuery({
    queryKey: ['sync-problems', resource.path],
    queryFn: () => resource.deadLetters.list(),
    subscribe: (invalidate: () => void) => resource.deadLetters.subscribe(invalidate),
  }, resource.queryClient)
  const action = useMutation({
    mutationFn: async ({ problem, kind }: { problem: { id: string; statements: readonly string[] }; kind: 'discard' | 'reapply' }) => {
      if (kind === 'discard') await resource.deadLetters.discard(problem.id)
      else await reapplyDeadLetter(resource, problem)
      await problems.refetch()
    },
    onError: async () => { await problems.refetch() },
  })

  return (
    <section aria-label="Sync problems" className="flex flex-col gap-6">
      <div className="flex flex-col gap-2">
        <h2 className="text-lg font-semibold">Sync problems</h2>
        <p className="text-on-surface-secondary text-sm">
          These changes were refused and removed from this device. Re-apply runs them again on the current database.
        </p>
      </div>
      {problems.error ? <p role="alert" className="text-sm">{problems.error.message}</p> : null}
      {problems.data?.length === 0 ? <p className="bg-surface-1 text-on-surface-secondary rounded-xl p-5 text-sm">No sync problems</p> : null}
      <ul className="flex flex-col gap-4">
        {problems.data?.map(problem => (
          <li key={problem.id} data-testid={`sync-problem-${problem.id}`} className="border-separator flex flex-col gap-4 rounded-xl border p-4">
            <div className="flex flex-col gap-2">
              <time data-testid={`sync-problem-time-${problem.id}`} className="text-on-surface-secondary text-sm" dateTime={new Date(problem.createdAt).toISOString()}>{new Date(problem.createdAt).toLocaleString()}</time>
              <p className="text-sm">{problem.reason}</p>
            </div>
            <pre className="bg-surface-1 overflow-x-auto rounded-lg p-3 font-mono text-xs whitespace-pre-wrap break-words">{problem.statements.join(';\n')}</pre>
            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant="outlined" disabled={action.isPending} data-testid={`sync-problem-reapply-${problem.id}`} onClick={() => { action.mutate({ problem, kind: 'reapply' }) }}>Re-apply</Button>
              <Button size="sm" variant="text" semanticRole="secondary" disabled={action.isPending} data-testid={`sync-problem-discard-${problem.id}`} onClick={() => { action.mutate({ problem, kind: 'discard' }) }}>Discard</Button>
            </div>
          </li>
        ))}
      </ul>
      {action.error ? <p role="alert" className="text-sm">{action.error.message}</p> : null}
    </section>
  )
}
